import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { Task } from "@recobra/schemas/task";
import { FieldDescription } from "@recobra/ui/field";

import { TaskModalSection, TaskSummaryList, TaskSummaryRow } from "../task-display";
import { DvcSearchSection } from "../task-form";
import { RequestedDocumentsSection } from "../task-form";
import { TaskMetadataFieldActions } from "./TaskMetadataFieldActions";
import type { TaskMetadataEditor } from "./useTaskMetadataEditor";

export const TaskDvcMetadata: FC<{ task: Task; editor: TaskMetadataEditor }> = ({
  task,
  editor,
}) => {
  const actions = <TaskMetadataFieldActions field="dvcSearchDetails" editor={editor} />;

  if (editor.editing === "dvcSearchDetails") {
    return (
      <TaskModalSection label={m.task_field_dvc_search()} actions={actions}>
        <DvcSearchSection
          form={editor.form}
          contextType={task.contextType}
          contextId={task.contextId}
          editingTaskId={task.id}
        />
      </TaskModalSection>
    );
  }

  return (
    <TaskModalSection>
      <TaskSummaryList>
        <TaskSummaryRow label={m.task_dvc_search_type_label()} action={actions}>
          {task.dvcSearchDetails ? (
            <div className="flex min-w-0 items-baseline gap-1.5">
              <span className="shrink-0">
                {m.dvc_search_type_label({ dvcSearchType: task.dvcSearchDetails.type })}
              </span>
              <span className="truncate font-mono">{task.dvcSearchDetails.dataToSearch}</span>
            </div>
          ) : (
            m.common_em_dash()
          )}
        </TaskSummaryRow>
      </TaskSummaryList>
    </TaskModalSection>
  );
};

/** Only for a task with no requested documents yet; otherwise the checklist group owns this editor. */
export const TaskRequestedDocumentsMetadata: FC<{ task: Task; editor: TaskMetadataEditor }> = ({
  task,
  editor,
}) => (
  <TaskModalSection
    label={m.task_requested_documents_label()}
    actions={<TaskMetadataFieldActions field="requestedDocuments" editor={editor} />}
  >
    {editor.editing === "requestedDocuments" ? (
      <RequestedDocumentsSection
        form={editor.form}
        contextType={task.contextType}
        contextId={task.contextId}
        hideLabel
      />
    ) : (
      <FieldDescription>{m.task_requested_documents_empty()}</FieldDescription>
    )}
  </TaskModalSection>
);
